/**
 * Market hours utility for US stock market
 * Regular trading hours: 9:30 AM - 4:00 PM ET, Monday-Friday
 * Excludes major US holidays
 */

export class MarketHours {
  private static readonly MARKET_OPEN_HOUR = 9;
  private static readonly MARKET_OPEN_MINUTE = 30;
  private static readonly MARKET_CLOSE_HOUR = 16;
  private static readonly MARKET_CLOSE_MINUTE = 0;
  
  // Extended hours: Pre-market 4:00 AM - 9:30 AM, Post-market 4:00 PM - 8:00 PM ET
  private static readonly PRE_MARKET_OPEN_HOUR = 4;
  private static readonly PRE_MARKET_OPEN_MINUTE = 0;
  private static readonly POST_MARKET_CLOSE_HOUR = 20;
  private static readonly POST_MARKET_CLOSE_MINUTE = 0;

  /**
   * Check if a date is a US market holiday
   */
  private static isMarketHoliday(date: Date): boolean {
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed
    const day = date.getDate();
    
    // Fixed date holidays
    const fixedHolidays = [
      { month: 1, day: 1 },   // New Year's Day
      { month: 7, day: 4 },   // Independence Day
      { month: 12, day: 25 }, // Christmas Day
    ];
    
    for (const holiday of fixedHolidays) {
      if (month === holiday.month && day === holiday.day) {
        return true;
      }
    }
    
    // Martin Luther King Jr. Day - 3rd Monday of January
    if (month === 1 && date.getDay() === 1) {
      const firstMonday = 1 + ((8 - new Date(year, 0, 1).getDay()) % 7);
      if (day === firstMonday + 14) return true;
    }
    
    // Presidents' Day - 3rd Monday of February
    if (month === 2 && date.getDay() === 1) {
      const firstMonday = 1 + ((8 - new Date(year, 1, 1).getDay()) % 7);
      if (day === firstMonday + 14) return true;
    }
    
    // Good Friday - Friday before Easter (complex calculation)
    // Memorial Day - Last Monday of May
    if (month === 5 && date.getDay() === 1) {
      const lastMonday = 31 - ((new Date(year, 5, 0).getDay() + 6) % 7);
      if (day >= lastMonday - 6 && day <= lastMonday) return true;
    }
    
    // Juneteenth - June 19th
    if (month === 6 && day === 19) return true;
    
    // Labor Day - 1st Monday of September
    if (month === 9 && date.getDay() === 1) {
      const firstMonday = 1 + ((8 - new Date(year, 8, 1).getDay()) % 7);
      if (day === firstMonday) return true;
    }
    
    // Thanksgiving - 4th Thursday of November
    if (month === 11 && date.getDay() === 4) {
      const firstThursday = 1 + ((11 - new Date(year, 10, 1).getDay()) % 7);
      if (day === firstThursday + 21) return true;
    }
    
    return false;
  }

  /**
   * Check if the market is currently open
   */
  static isMarketOpen(): boolean {
    const now = new Date();
    const et = this.convertToET(now);
    
    // Check if it's a weekday (Monday = 1, Friday = 5)
    const dayOfWeek = et.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return false; // Weekend
    }
    
    // Check if it's a market holiday
    if (this.isMarketHoliday(et)) {
      return false;
    }

    // Check if it's within trading hours
    const currentHour = et.getHours();
    const currentMinute = et.getMinutes();
    const currentTimeMinutes = currentHour * 60 + currentMinute;
    
    const marketOpenMinutes = this.MARKET_OPEN_HOUR * 60 + this.MARKET_OPEN_MINUTE;
    const marketCloseMinutes = this.MARKET_CLOSE_HOUR * 60 + this.MARKET_CLOSE_MINUTE;
    
    return currentTimeMinutes >= marketOpenMinutes && currentTimeMinutes < marketCloseMinutes;
  }

  /**
   * Check if extended hours trading is active (pre-market 4 AM - 9:30 AM or post-market 4 PM - 8 PM ET)
   */
  static isExtendedHoursActive(): boolean {
    const now = new Date();
    const et = this.convertToET(now);
    
    // Check if it's a weekday
    const dayOfWeek = et.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return false; // Weekend - no extended hours
    }
    
    // Check if it's a market holiday
    if (this.isMarketHoliday(et)) {
      return false;
    }

    const currentHour = et.getHours();
    const currentMinute = et.getMinutes();
    const currentTimeMinutes = currentHour * 60 + currentMinute;
    
    const preMarketOpenMinutes = this.PRE_MARKET_OPEN_HOUR * 60 + this.PRE_MARKET_OPEN_MINUTE;
    const marketOpenMinutes = this.MARKET_OPEN_HOUR * 60 + this.MARKET_OPEN_MINUTE;
    const marketCloseMinutes = this.MARKET_CLOSE_HOUR * 60 + this.MARKET_CLOSE_MINUTE;
    const postMarketCloseMinutes = this.POST_MARKET_CLOSE_HOUR * 60 + this.POST_MARKET_CLOSE_MINUTE;
    
    // Pre-market: 4:00 AM - 9:30 AM
    const isPreMarket = currentTimeMinutes >= preMarketOpenMinutes && currentTimeMinutes < marketOpenMinutes;
    
    // Post-market: 4:00 PM - 8:00 PM
    const isPostMarket = currentTimeMinutes >= marketCloseMinutes && currentTimeMinutes < postMarketCloseMinutes;
    
    return isPreMarket || isPostMarket;
  }

  /**
   * Check if any trading activity is happening (regular hours OR extended hours)
   */
  static isTradingActive(): boolean {
    return this.isMarketOpen() || this.isExtendedHoursActive();
  }

  /**
   * Get time until market opens (in minutes)
   * Returns 0 if market is currently open
   * Returns negative if market is closed for the day
   */
  static getMinutesUntilMarketOpen(): number {
    const now = new Date();
    const et = this.convertToET(now);
    
    if (this.isMarketOpen()) {
      return 0;
    }

    const currentHour = et.getHours();
    const currentMinute = et.getMinutes();
    const currentTimeMinutes = currentHour * 60 + currentMinute;
    
    const marketOpenMinutes = this.MARKET_OPEN_HOUR * 60 + this.MARKET_OPEN_MINUTE;
    const marketCloseMinutes = this.MARKET_CLOSE_HOUR * 60 + this.MARKET_CLOSE_MINUTE;
    
    // If before market open today
    if (currentTimeMinutes < marketOpenMinutes) {
      return marketOpenMinutes - currentTimeMinutes;
    }
    
    // If after market close, calculate time until next market day
    // This is a simplified version - doesn't account for holidays
    const dayOfWeek = et.getDay();
    let daysUntilNextMarket = 1;
    
    if (dayOfWeek === 5) { // Friday
      daysUntilNextMarket = 3; // Skip to Monday
    } else if (dayOfWeek === 6) { // Saturday
      daysUntilNextMarket = 2; // Skip to Monday
    }
    
    const minutesUntilMidnight = (24 * 60) - currentTimeMinutes;
    const minutesFromMidnightToOpen = marketOpenMinutes;
    const minutesForFullDays = (daysUntilNextMarket - 1) * 24 * 60;
    
    return minutesUntilMidnight + minutesFromMidnightToOpen + minutesForFullDays;
  }

  /**
   * Get time until market closes (in minutes)
   * Returns 0 if market is closed
   */
  static getMinutesUntilMarketClose(): number {
    if (!this.isMarketOpen()) {
      return 0;
    }

    const now = new Date();
    const et = this.convertToET(now);
    
    const currentHour = et.getHours();
    const currentMinute = et.getMinutes();
    const currentTimeMinutes = currentHour * 60 + currentMinute;
    
    const marketCloseMinutes = this.MARKET_CLOSE_HOUR * 60 + this.MARKET_CLOSE_MINUTE;
    
    return marketCloseMinutes - currentTimeMinutes;
  }

  /**
   * Convert UTC time to Eastern Time (handles DST automatically)
   */
  private static convertToET(date: Date): Date {
    // Use Intl.DateTimeFormat to handle DST automatically
    const etString = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',  
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(date);
    
    // Parse the formatted string back to a Date object
    const [datePart, timePart] = etString.split(', ');
    const [month, day, year] = datePart.split('/');
    const [hour, minute, second] = timePart.split(':');
    
    return new Date(
      parseInt(year),
      parseInt(month) - 1, // Month is 0-indexed
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second)
    );
  }

  /**
   * Get market status as a human-readable string
   */
  static getMarketStatusString(): string {
    if (this.isMarketOpen()) {
      const minutesUntilClose = this.getMinutesUntilMarketClose();
      const hours = Math.floor(minutesUntilClose / 60);
      const minutes = minutesUntilClose % 60;
      return `Market OPEN - Closes in ${hours}h ${minutes}m`;
    } else {
      const minutesUntilOpen = this.getMinutesUntilMarketOpen();
      if (minutesUntilOpen < 24 * 60) {
        const hours = Math.floor(minutesUntilOpen / 60);
        const minutes = minutesUntilOpen % 60;
        return `Market CLOSED - Opens in ${hours}h ${minutes}m`;
      } else {
        return `Market CLOSED - Opens next trading day`;
      }
    }
  }
}